import { createSelector } from 'reselect';
import moment from 'moment';
import Immutable from 'seamless-immutable';
import { addIndex, filter, flatten, forEach, isEmpty, join, last, map, path, pathOr, pipe, prop, propEq, reduce, reject, sort, sortBy } from 'ramda';
import { PermissionService, SessionSelectors } from 'invision-core';
import MetadataConstants from 'invision-core/src/components/metadata/metadata.constants';
import MetadataSelectors from 'invision-core/src/components/metadata/metadata.selectors';
import { IsDbss } from 'invision-core/src/components/session/businessunit.selectors';
import i18n from 'invision-core/src/components/i18n/i18n';
import { CODES } from 'invision-core/src/components/metadata/codes/codes.constants';
import { getFormattedServiceAttributeValue } from './services.list.selectors.helper';
import CustomerCareLocaleKeys from '../../locales/keys';
import { getBoolOrDefault } from 'invision-core/src/components/helpers/bool.helper';
import { SelectedCustomerSelector } from './customer.selectors';
import { ConvergentBillerSubscriberSummaryServicesSelector } from './customer.convergent.biller.selectors';
import { findLastSelectedRating, getFormattedName, sortMemberListByAlpheticalOrder } from '../../components/customer/household/household.helpers';
import { HOUSEHOLD_ADMIN_ACCESS } from '../../security.attributes';
import { PaymentInstrumentsWithTypeNamesViewModel } from './customer.ewallet.selectors';
import { ACTIVATE_MEMBER_OPTION_ID, EDIT_PRIVILEGES_ID, HOUSEHOLD_OPTION_ACTIVATE_ID, HOUSEHOLD_OPTION_EDIT_ID, HOUSEHOLD_OPTION_REMOVE_ID, REMOVE_MEMBER_OPTION_ID } from '../../components/customer/household/household.constants';
import { NA } from '../constants/billing.payments.constants';
import { createImmutableSelector } from 'invision-core/src/utilities/create.immutable.selector';
import { MetadataCodeTypeDictionarySelector } from 'invision-core/src/components/metadata/codes/codes.selectors';
var EMPTY_ARRAY = [];
var IMMUTABLE_EMPTY_ARRAY = Immutable([]);
var IMMUTABLE_EMPTY_OBJECT = Immutable({});
var ACTIVE = 1;
var REMOVED = 2;
export var ACTIVATE_HOUSEHOLD = {
  id: HOUSEHOLD_OPTION_ACTIVATE_ID,
  title: CustomerCareLocaleKeys.HOUSEHOLD.REACTIVATE_HOUSEHOLD
};
export var ACTIVATE_MEMBER = {
  id: ACTIVATE_MEMBER_OPTION_ID,
  title: CustomerCareLocaleKeys.HOUSEHOLD.REACTIVATE_MEMBER
};
export var EDIT_HOUSEHOLD = {
  id: HOUSEHOLD_OPTION_EDIT_ID,
  title: CustomerCareLocaleKeys.HOUSEHOLD.EDIT
};
export var EDIT_PRIVILEGES = {
  id: EDIT_PRIVILEGES_ID,
  title: CustomerCareLocaleKeys.HOUSEHOLD.EDIT_PRIVILEGES
};
export var REMOVE_HOUSEHOLD = {
  id: HOUSEHOLD_OPTION_REMOVE_ID,
  title: CustomerCareLocaleKeys.HOUSEHOLD.REMOVE_HOUSEHOLD
};
export var REMOVE_MEMBER = {
  id: REMOVE_MEMBER_OPTION_ID,
  title: CustomerCareLocaleKeys.HOUSEHOLD.REMOVE_MEMBER
};
export var HOUSEHOLD_ACCESS = {
  ENABLE_PROMOTE_TO_FULL_ACCESS: 'enable_promote_to_full_access',
  ENABLE_ACCESS_CONTROL: 'enable_access_control',
  ENABLE_PARENTAL_CONTROL: 'enable_parental_control',
  ENABLE_SERVICE_LEVEL_MANAGEMENT: 'enable_service_level_management'
};

//Returns if the CSR can access households via Security Attributes
//BusinessUnit enabled is checked via IsBusinessUnitEnabled in customer.actions.template.selectors
export var CsrHasHouseholdAdminAccessSelector = createSelector([SessionSelectors.UserSecurityAttributesSelector], function () {
  var securityAttributeSelector = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return PermissionService.hasAdminAccess(securityAttributeSelector, HOUSEHOLD_ADMIN_ACCESS);
});
var recoverableUIStateSelector = function recoverableUIStateSelector(state) {
  return pathOr(null, ['customercare', 'recoverableUiState', 'household'], state);
};
export var CurrentHouseholdSelector = createSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return selectedCustomer.household.data;
});
export var EditHouseholdDataSelector = createSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return selectedCustomer.household.editHouseholdData;
});
var removeAssociatedServiceFromAvailable = function removeAssociatedServiceFromAvailable(availableServices, serviceToBeRemoved) {
  return reject(propEq(serviceToBeRemoved.ServiceIdentifier.Value, 'serviceValue'), availableServices);
};
var RecoverableMemberPrivilegesSelector = function RecoverableMemberPrivilegesSelector(state) {
  return pathOr(null, ['customercare', 'recoverableUiState', 'memberPrivileges'], state);
};
export var RecoverablePrivilegesSelector = createSelector([RecoverableMemberPrivilegesSelector], function (uiState) {
  return uiState.privileges;
});
export var HouseholdServicesAssociations = createSelector([CurrentHouseholdSelector, ConvergentBillerSubscriberSummaryServicesSelector, MetadataCodeTypeDictionarySelector(CODES.RegularExpression), MetadataCodeTypeDictionarySelector(CODES.ServiceAttribute)], function (currentHousehold, subscriberServices, regularExpressionCodeTable, serviceAttributeCodeTable) {
  var householdServicesAssociations = [];
  if (currentHousehold && currentHousehold.MemberToServiceAssociations) {
    householdServicesAssociations = currentHousehold.MemberToServiceAssociations.map(function (memberToServiceAssociation) {
      // !TODO: Optimize
      if (subscriberServices) {
        subscriberServices = removeAssociatedServiceFromAvailable(subscriberServices, memberToServiceAssociation);
      }
      return Object.assign({}, memberToServiceAssociation.asMutable(), {
        ServiceFriendlyName: memberToServiceAssociation.ServiceIdentifier.FriendlyName || memberToServiceAssociation.ServiceIdentifier.Value,
        ServiceIdentifier: memberToServiceAssociation.ServiceIdentifier.Value,
        ServiceIdentifierFormatted: getFormattedServiceAttributeValue(memberToServiceAssociation.ServiceIdentifier.ServiceAttributeId, memberToServiceAssociation.ServiceIdentifier.Value, serviceAttributeCodeTable, regularExpressionCodeTable)
      });
    });
  }
  if (currentHousehold && subscriberServices) {
    householdServicesAssociations = householdServicesAssociations.concat(subscriberServices.map(function (subscriberService) {
      return {
        ServiceFriendlyName: subscriberService.serviceFriendlyName || subscriberService.serviceValue,
        ServiceIdentifier: subscriberService.serviceValue,
        ServiceIdentifierFormatted: subscriberService.serviceValueFormatted,
        SubscriberId: currentHousehold.HeadOfHouseholdSubscriberId
      };
    }));
  }
  return householdServicesAssociations;
});

//Returns all Household Members
export var HouseholdMembersSelector = createSelector([CurrentHouseholdSelector, ConvergentBillerSubscriberSummaryServicesSelector, MetadataCodeTypeDictionarySelector(CODES.RegularExpression), MetadataCodeTypeDictionarySelector(CODES.ServiceAttribute)], function (currentHousehold, subscriberServices, regularExpressionCodeTable, serviceAttributeCodeTable) {
  var householdMembers = [];
  if (currentHousehold && currentHousehold.Members.length) {
    var membersServicesAssociations = {};
    if (currentHousehold.MemberToServiceAssociations) {
      currentHousehold.MemberToServiceAssociations.forEach(function (memberToServiceAssociation) {
        if (!membersServicesAssociations[memberToServiceAssociation.SubscriberId]) {
          membersServicesAssociations[memberToServiceAssociation.SubscriberId] = [];
        }

        // !TODO: Optimize
        if (subscriberServices) {
          subscriberServices = removeAssociatedServiceFromAvailable(subscriberServices, memberToServiceAssociation);
        }
        membersServicesAssociations[memberToServiceAssociation.SubscriberId].push({
          ServiceFriendlyName: memberToServiceAssociation.ServiceIdentifier.FriendlyName || memberToServiceAssociation.ServiceIdentifier.Value,
          ServiceIdentifier: memberToServiceAssociation.ServiceIdentifier.Value,
          ServiceIdentifierFormatted: getFormattedServiceAttributeValue(memberToServiceAssociation.ServiceIdentifier.ServiceAttributeId, memberToServiceAssociation.ServiceIdentifier.Value, serviceAttributeCodeTable, regularExpressionCodeTable)
        });
      });
    }
    householdMembers = currentHousehold.Members.map(function (member) {
      var memberServices = membersServicesAssociations[member.SubscriberId] || [];
      if (member.SubscriberId === currentHousehold.HeadOfHouseholdSubscriberId && path(['length'], subscriberServices)) {
        memberServices = memberServices.concat(subscriberServices.map(function (subscriberService) {
          return {
            ServiceFriendlyName: subscriberService.serviceFriendlyName || subscriberService.serviceValue,
            ServiceIdentifier: subscriberService.serviceValue,
            ServiceIdentifierFormatted: subscriberService.serviceValueFormatted
          };
        }));
      }
      return member.set('formattedName', getFormattedName(member)).set('services', memberServices);
    });
  }
  return householdMembers;
});
export var SelectedHouseholdMemberIdSelector = createSelector([recoverableUIStateSelector], function (uiState) {
  return uiState ? uiState.selectedHouseholdMemberId : null;
});
export var HeadOfHousholdSubscriberIdSelector = createSelector([CurrentHouseholdSelector], function (currentHousehold) {
  return currentHousehold ? currentHousehold.HeadOfHouseholdSubscriberId : null;
});
export var IncludeRemovedSelector = createSelector([recoverableUIStateSelector], function (uiState) {
  return uiState && uiState.filterData ? uiState.filterData.includeRemoved : false;
});

//Returns all household members that are NOT head of household
export var HouseholdChildMembersSelector = createSelector([HeadOfHousholdSubscriberIdSelector, HouseholdMembersSelector, IncludeRemovedSelector], function (hohSubscriberId, members, showRemoved) {
  return members.filter(function (member) {
    return showRemoved ? member.SubscriberId !== hohSubscriberId : member.SubscriberId !== hohSubscriberId && member.Status === ACTIVE;
  });
});
export var HeadOfHouseholdSelector = createSelector([HeadOfHousholdSubscriberIdSelector, HouseholdMembersSelector], function (hohSubscriberId, members) {
  return members.find(function (member) {
    return member.SubscriberId === hohSubscriberId;
  }) || null;
});
export var HouseholdChildMembersSortedByName = createSelector([HeadOfHouseholdSelector, HouseholdChildMembersSelector], function (headOfHousehold, members) {
  if (members.length) {
    var mutableMembers = members.asMutable({
      deep: true
    });
    return sortMemberListByAlpheticalOrder(mutableMembers, headOfHousehold, false);
  } else {
    return IMMUTABLE_EMPTY_ARRAY;
  }
});
export var ShowHouseholdSelector = createImmutableSelector([CurrentHouseholdSelector, IncludeRemovedSelector], function (currentHousehold, includeRemoved) {
  return includeRemoved ? includeRemoved : currentHousehold && currentHousehold.Status ? currentHousehold.Status === ACTIVE : true;
});
export var HousholdMembersSorted = createSelector([HeadOfHouseholdSelector, HouseholdChildMembersSortedByName, IncludeRemovedSelector], function (headOfHousehold, children, showRemoved) {
  return headOfHousehold ? [headOfHousehold].concat(children.filter(function (child) {
    return showRemoved || child.Status === ACTIVE;
  })) : IMMUTABLE_EMPTY_ARRAY;
});
export var HousholdMembersSortedForServiceAssociation = createSelector([HeadOfHouseholdSelector, HouseholdChildMembersSortedByName], function (headOfHousehold, children) {
  return headOfHousehold ? [headOfHousehold].concat(children.filter(function (child) {
    return child.Status === ACTIVE;
  })) : IMMUTABLE_EMPTY_ARRAY;
});
export var HouseholdMembersListSelector = createSelector([HousholdMembersSorted, IncludeRemovedSelector], function (members, showRemoved) {
  var filteredMembers = members.filter(function (member) {
    return showRemoved || member.Status === ACTIVE;
  });
  return members.length ? Immutable([{
    isOverview: true
  }]).concat(filteredMembers) : IMMUTABLE_EMPTY_ARRAY;
});

//Returns the household member selected in the richlist
export var SelectedHouseholdMemberSelector = createSelector([SelectedHouseholdMemberIdSelector, HouseholdMembersListSelector], function (selectedHouseholdMemberId, members) {
  if (selectedHouseholdMemberId) {
    return members.find(function (_ref) {
      var SubscriberId = _ref.SubscriberId;
      return SubscriberId === selectedHouseholdMemberId;
    });
  } else {
    return members.find(function (_ref2) {
      var isOverview = _ref2.isOverview;
      return isOverview;
    }) || null;
  }
});
export var SelectedHouseholdMemberServicesSelector = createImmutableSelector([SelectedHouseholdMemberSelector], function (selectedMember) {
  if (selectedMember && selectedMember.services && selectedMember.services.length) {
    return selectedMember.services.filter(function (service) {
      return service.ServiceFriendlyName !== NA;
    });
  }
  return EMPTY_ARRAY;
});

//Returns the household member that corresponds to the looked up subscriberId
export var CurrentHouseholdMemberSelector = createSelector([SelectedCustomerSelector, HouseholdMembersSelector], function (selectedCustomer, members) {
  return members.length > 0 ? members.find(function (member) {
    return member.SubscriberId === selectedCustomer.data.Id && member.Status === ACTIVE;
  }) : null;
});
export var SelectedMemberIsCurrentMemberSelector = createSelector([SelectedHouseholdMemberSelector, CurrentHouseholdMemberSelector], function (selectedMember, currentMember) {
  return selectedMember && currentMember && (selectedMember.SubscriberId || currentMember.SubscriberId) ? selectedMember.SubscriberId === currentMember.SubscriberId : false;
});
export var HeadOfHouseholdIsSelectedSelector = createSelector([SelectedHouseholdMemberSelector, HeadOfHousholdSubscriberIdSelector], function (selectedMember, hohSubscriberId) {
  return selectedMember && (selectedMember.SubscriberId || hohSubscriberId) ? selectedMember.SubscriberId === hohSubscriberId : false;
});
export var CurrentMemberIsHeadOfHouseholdSelector = createSelector([CurrentHouseholdMemberSelector, HeadOfHouseholdSelector], function (currentMember, headOfHousehold) {
  return currentMember && headOfHousehold && (currentMember.SubscriberId || headOfHousehold.SubscriberId) ? currentMember.SubscriberId === headOfHousehold.SubscriberId : false;
});
export var CanMakeHouseholdChangesSelector = createSelector([CurrentMemberIsHeadOfHouseholdSelector, CsrHasHouseholdAdminAccessSelector], function (isHoH, hasAdminAccess) {
  return isHoH && hasAdminAccess;
});
export var HouseholdErrorSelector = createSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return selectedCustomer.household.householdError;
});
export var HouseholdStatusSelector = createSelector([CurrentHouseholdSelector], function (household) {
  return household ? household.Status : null;
});
export var HouseholdMoreOptionsSelector = createSelector([HouseholdStatusSelector, IsDbss], function (householdStatus, isDbss) {
  if (!householdStatus) {
    return EMPTY_ARRAY;
  }
  return createMenuItems([ACTIVATE_HOUSEHOLD, EDIT_HOUSEHOLD, REMOVE_HOUSEHOLD]).filter(function (item) {
    return !(householdStatus === ACTIVE && item.id === ACTIVATE_HOUSEHOLD.id) && !(householdStatus === REMOVED && item.id === REMOVE_HOUSEHOLD.id) && !(isDbss && item.id === REMOVE_HOUSEHOLD.id);
  });
});
export var MemberStatusSelector = createSelector([SelectedHouseholdMemberSelector], function (selectedMember) {
  return selectedMember ? selectedMember.Status : null;
});
export var HouseholdMemberMoreOptionsSelector = createSelector([MemberStatusSelector, IsDbss], function (memberStatus, isDbss) {
  if (!memberStatus) {
    return EMPTY_ARRAY;
  }
  return createMenuItems([ACTIVATE_MEMBER, EDIT_PRIVILEGES, REMOVE_MEMBER]).filter(function (item) {
    return !(memberStatus === ACTIVE && item.id === ACTIVATE_MEMBER.id) && !(memberStatus === REMOVED && item.id === REMOVE_MEMBER.id) && !(isDbss && item.id === ACTIVATE_MEMBER.id);
  });
});
var createMenuItems = function createMenuItems(options) {
  return options.map(function (_ref3) {
    var id = _ref3.id,
      title = _ref3.title;
    return {
      id: id,
      title: i18n.translate(title)
    };
  });
};
export var IsCreatingHouseholdSelector = createSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return selectedCustomer.household.isCreatingHousehold;
});
export var IsCreatingHouseholdMemberSelector = createSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return selectedCustomer.household.isCreatingHouseholdMember;
});
export var IsRemovingHouseholdMemberSelector = createSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return selectedCustomer.household.isRemovingHouseholdMember;
});
export var IsFetchingHouseholdSelector = createSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return selectedCustomer.household.isFetchingHousehold;
});
export var IsRemovingHouseholdSelector = createSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return selectedCustomer.household.isRemovingHousehold;
});
export var IsUpdatingHouseholdSelector = createSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return selectedCustomer.household.isUpdatingHousehold;
});
var commaDelimit = join(', ');
var mapIndexToDayOfWeek = addIndex(map)(function (i, index) {
  return moment().weekday(index).format('dddd');
});
var getDayOfWeekLabels = function getDayOfWeekLabels() {
  return mapIndexToDayOfWeek(new Array(7));
};
var mapAPIBlockToDisplayString = map(function (usageBlock) {
  var start = moment().hour(usageBlock.StartingHour);
  start.minute(usageBlock.StartingMinute);
  var end = moment(start).add(usageBlock.DurationMinutes, 'minutes');
  return "".concat(start.format('ha'), "-").concat(end.format('ha'));
});
var indexedForEach = addIndex(forEach);
var getUsagePrivilegeDisplayData = function getUsagePrivilegeDisplayData() {
  var usage = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
  var displayData = {};
  indexedForEach(function (item, index) {
    var usageBlocksForCurrentDayOfWeek = sortBy(prop('StartingHour'))(filter(propEq(index, 'DayOfWeek'))(usage));
    var timeEntries = mapAPIBlockToDisplayString(usageBlocksForCurrentDayOfWeek);
    displayData[item] = timeEntries.length ? commaDelimit(timeEntries) : i18n.translate(CustomerCareLocaleKeys.NOT_APPLICABLE);
  })(getDayOfWeekLabels());
  return displayData;
};
var getPrivilegesFromMember = function getPrivilegesFromMember(member) {
  if (isEmpty(pathOr({}, ['Privileges'], member))) {
    return IMMUTABLE_EMPTY_OBJECT;
  }
  var privileges = member.Privileges;
  var Timezone = privileges.Timezone !== null && privileges.Timezone !== undefined ? String(privileges.Timezone) : null;
  return privileges.set('UsagePrivilegeDisplay', getUsagePrivilegeDisplayData(privileges.DayAndTimePrivileges)).set('Timezone', Timezone).set('LimitSpending', privileges.SpendingLimitAmount !== undefined);
};
export var IsUpdatingHouseholdServicesAssociationsSelector = createSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return selectedCustomer.household.isUpdatingHouseholdServicesAssociations;
});
export var SelectedMemberPrivilegesSelector = createSelector([SelectedHouseholdMemberSelector], function (selectedMember) {
  return getPrivilegesFromMember(selectedMember);
});
export var RecoverablePaymentInstrumentsSelector = createSelector([RecoverableMemberPrivilegesSelector], function (uiState) {
  return uiState.paymentInstruments ? uiState.paymentInstruments : EMPTY_ARRAY;
});
export var RecoverableSelectedHoursSelector = createSelector([RecoverableMemberPrivilegesSelector], function (uiState) {
  return uiState.selectedHours ? uiState.selectedHours : EMPTY_ARRAY;
});
var toAPIRequest = map(function (_ref4) {
  var hour = _ref4.hour,
    days = _ref4.days;
  return map(function (_ref5) {
    var day = _ref5.day,
      selected = _ref5.selected;
    return {
      StartingHour: hour,
      DayOfWeek: day,
      selected: selected,
      DurationMinutes: 60
    };
  })(days);
});
var sortByDayAndHour = sort(function (a, b) {
  if (a.DayOfWeek === b.DayOfWeek) {
    return a.StartingHour > b.StartingHour ? 1 : -1;
  } else {
    return a.DayOfWeek > b.DayOfWeek ? 1 : -1;
  }
});
var combineNearbySelections = reduce(function (a, b) {
  var lastBlock = last(a);
  if (!b.selected) {
    return a;
  } else if (lastBlock && lastBlock.DayOfWeek === b.DayOfWeek && b.StartingHour - lastBlock.StartingHour === lastBlock.DurationMinutes / 60) {
    return a.setIn([a.length - 1, 'DurationMinutes'], lastBlock.DurationMinutes + 60);
  } else {
    return a.concat(b);
  }
}, IMMUTABLE_EMPTY_ARRAY);
var cleanForAPI = map(function (_ref6) {
  var DayOfWeek = _ref6.DayOfWeek,
    StartingHour = _ref6.StartingHour,
    DurationMinutes = _ref6.DurationMinutes;
  return {
    DayOfWeek: DayOfWeek,
    StartingHour: StartingHour,
    DurationMinutes: DurationMinutes,
    StartingMinute: 0
  };
});
var selectedHourViewModelToAPI = pipe(toAPIRequest, flatten, sortByDayAndHour, combineNearbySelections, cleanForAPI);
export var SelectedMemberPrivilegesAPIRequestSelector = createSelector([RecoverablePrivilegesSelector, RecoverablePaymentInstrumentsSelector, RecoverableSelectedHoursSelector], function () {
  var privileges = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : IMMUTABLE_EMPTY_OBJECT;
  var paymentInstruments = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : IMMUTABLE_EMPTY_ARRAY;
  var selectedHours = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : IMMUTABLE_EMPTY_ARRAY;
  var selectedPrivileges = privileges;
  selectedPrivileges = selectedPrivileges.set('DayAndTimePrivileges', selectedHourViewModelToAPI(selectedHours));
  if (!selectedPrivileges.LimitSpending) {
    selectedPrivileges = selectedPrivileges.set('LimitSpending', undefined).set('SpendingBalanceAccumulates', undefined).set('SpendingLimitAmount', undefined).set('SpendingLimitPeriodDays', undefined).set('SpendingBalance', undefined);
  }
  if (selectedPrivileges.EnforcePaymentPrivileges) {
    selectedPrivileges = selectedPrivileges.set('PaymentInstrumentPrivileges', paymentInstruments.reduce(function (prev, _ref7) {
      var selected = _ref7.selected,
        Id = _ref7.Id;
      return selected ? prev.concat(Id) : prev;
    }, []));
  }
  return selectedPrivileges.without(['timeZone', 'UsagePrivilegeDisplay']);
});
export var CurrentMemberPrivilegesSelector = createSelector([CurrentHouseholdMemberSelector], function (currentMember) {
  return getPrivilegesFromMember(currentMember);
});
export var CurrentMemberHasCreatePaymentInstrumentPermissionsSelector = createSelector([CurrentMemberPrivilegesSelector, CurrentMemberIsHeadOfHouseholdSelector], function (currentMemberPrivileges, selectedMemberIsHeadOfHousehold) {
  var enforcePaymentPrivilegesSetForSelectedMember = pathOr(false, ['EnforcePaymentPrivileges'], currentMemberPrivileges);
  if (selectedMemberIsHeadOfHousehold || isEmpty(currentMemberPrivileges)) {
    return true;
  }

  // If EnforcePaymentPrivileges = false, we should NOT be allowing creation of new Payment Methods
  if (!enforcePaymentPrivilegesSetForSelectedMember) {
    return false;
  }
  return pathOr(false, ['CreatePaymentInstrumentEnabled'], currentMemberPrivileges);
});
export var SelectedMemberPaymentPrivilegesSelector = createSelector([SelectedMemberPrivilegesSelector], function (privileges) {
  return pathOr(EMPTY_ARRAY, ['PaymentInstrumentPrivileges'], privileges);
});
export var SelectedMemberPaymentPrivilegesViewModelsSelector = createSelector([SelectedMemberPaymentPrivilegesSelector, PaymentInstrumentsWithTypeNamesViewModel], function (paymentInstrumentPrivileges, paymentInstrumentsViewModels) {
  if (isEmpty(paymentInstrumentPrivileges) || isEmpty(paymentInstrumentsViewModels)) {
    return EMPTY_ARRAY;
  }
  return paymentInstrumentsViewModels.filter(function (paymentInstrument) {
    return paymentInstrumentPrivileges.some(function (privilegeId) {
      return privilegeId === paymentInstrument.Id;
    });
  });
});
export var RatingsByCategorySelector = createSelector([MetadataSelectors.codes.MetadataCodeLoadedSelector(MetadataConstants.codes.Rating), MetadataSelectors.codes.MetadataCodeSelector(MetadataConstants.codes.Rating), MetadataSelectors.codes.MetadataCodeLoadedSelector(MetadataConstants.codes.RatingCategory), MetadataSelectors.codes.MetadataCodeSelector(MetadataConstants.codes.RatingCategory)], function (hasLoadedRatings, ratings, hasLoadedCategories, categories) {
  if (hasLoadedCategories && hasLoadedRatings) {
    var ratingsByCategoryMap = {};
    ratings.items.forEach(function (rating) {
      var categoryCode = rating.AdditionalProperties.find(function (_ref8) {
        var Key = _ref8.Key;
        return Key === 'category_code';
      }).Value;
      ratingsByCategoryMap[categoryCode] = ratingsByCategoryMap[categoryCode] ? ratingsByCategoryMap[categoryCode].concat(rating) : [rating];
    });
    return categories.items.map(function (_ref9) {
      var Value = _ref9.Value,
        Description = _ref9.Description,
        Name = _ref9.Name;
      return {
        value: Value,
        description: Description,
        name: Name,
        selected: null,
        ratings: ratingsByCategoryMap[Value]
      };
    });
  }
  return EMPTY_ARRAY;
});
export var HouseholdPrivilegesSelector = createImmutableSelector([MetadataSelectors.codes.MetadataCodeLoadedSelector(MetadataConstants.codes.HouseholdPrivileges), MetadataSelectors.codes.MetadataCodeTypeSelector(MetadataConstants.codes.HouseholdPrivileges)], function (householdLoaded, household) {
  var privileges = null;
  if (householdLoaded) {
    privileges = household.find(function (property) {
      return property && !property.Global;
    });
    if (!privileges) {
      privileges = household.find(function (property) {
        return property;
      });
    }
  }
  return privileges ? privileges.AdditionalProperties : null;
});
export var EnableHouseholdFullAccessControlSelector = createImmutableSelector(HouseholdPrivilegesSelector, function (householdPrivileges) {
  var result = false;
  if (householdPrivileges) {
    result = getBoolOrDefault(householdPrivileges.find(function (property) {
      return property.Key === HOUSEHOLD_ACCESS.ENABLE_PROMOTE_TO_FULL_ACCESS;
    }).Value, false);
  }
  return result;
});
export var EnableHouseholdServiceLevelManagementSelector = createImmutableSelector(HouseholdPrivilegesSelector, function (householdPrivileges) {
  var result = false;
  if (householdPrivileges) {
    result = getBoolOrDefault(householdPrivileges.find(function (property) {
      return property.Key === HOUSEHOLD_ACCESS.ENABLE_SERVICE_LEVEL_MANAGEMENT;
    }).Value, false);
  }
  return result;
});
export var EnableHouseholdAccessPrivilegesSelector = createImmutableSelector(HouseholdPrivilegesSelector, function (householdPrivileges) {
  var result = false;
  if (householdPrivileges) {
    result = getBoolOrDefault(householdPrivileges.find(function (property) {
      return property.Key === HOUSEHOLD_ACCESS.ENABLE_PARENTAL_CONTROL;
    }).Value, false);
  }
  return result;
});
export var EnableHouseholdUsagePrivilegesSelector = createImmutableSelector(HouseholdPrivilegesSelector, function (householdPrivileges) {
  var result = false;
  if (householdPrivileges) {
    result = getBoolOrDefault(householdPrivileges.find(function (property) {
      return property.Key === HOUSEHOLD_ACCESS.ENABLE_ACCESS_CONTROL;
    }).Value, false);
  }
  return result;
});
export var SelectableRatingsByCategory = createSelector([RatingsByCategorySelector, SelectedMemberPrivilegesSelector], function (ratingsByCategory, memberPrivileges) {
  if (ratingsByCategory.length) {
    var SELECT_OPTION = {
      text: i18n.translate(CustomerCareLocaleKeys.SELECT),
      value: null
    };
    return ratingsByCategory.map(function (_ref10) {
      var name = _ref10.name,
        description = _ref10.description,
        ratings = _ref10.ratings,
        value = _ref10.value;
      var selectedRating = findLastSelectedRating(ratings, memberPrivileges.RatingPrivileges);
      return {
        description: description,
        selected: selectedRating ? selectedRating.Value : null,
        name: name,
        ratings: [SELECT_OPTION].concat(ratings.map(function (_ref11) {
          var Value = _ref11.Value,
            Name = _ref11.Name;
          return {
            text: Name,
            value: Value
          };
        })),
        value: value
      };
    });
  }
  return EMPTY_ARRAY;
});